import { TypedDocumentNode, gql } from '@apollo/client';
import { useMemo } from 'react';

import {
  FiatWalletAccountState,
  Rarity,
  SupportedCurrency,
} from '@sorare/core/src/__generated__/globalTypes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { withFragments } from '@sorare/core/src/gql';

import { useRewardsVerification_so5Reward } from './__generated__/useRewardsVerification.graphql';

/*
Rewards need verification before claim:
- Box rewards            -> phone number validation
- Card rewards           -> phone number validation
- Card rewards (Common)  -> phone number validation
- Cash rewards (FIAT)    -> KYC level 2 (VALIDATED_OWNER)
- Cash rewards (ETH)     -> No verification needed
*/

type RewardByType<T> = {
  box: T[];
  card: T[];
  cash: T[];
  total: number;
};

type DumbCard = { anyCard: { rarityTyped: Rarity } | null };
const rewardsAreCommon = <T extends { rewardCards: DumbCard[] }>({
  rewardCards,
}: T): boolean =>
  rewardCards.every(({ anyCard }) => anyCard?.rarityTyped === Rarity.common);
export const cardsAreCommon = <T extends DumbCard>({ anyCard }: T): boolean =>
  anyCard?.rarityTyped === Rarity.common;

export const useRewardsVerification = withFragments(
  <T extends useRewardsVerification_so5Reward>(so5Rewards: T[]) => {
    const { currentUser } = useCurrentUserContext();
    const { fiatWalletAccountable } = useCurrentUserContext();

    const kycCompleted =
      fiatWalletAccountable?.state === FiatWalletAccountState.VALIDATED_OWNER;
    const { phoneNumberVerified } = currentUser || {};

    return useMemo(() => {
      const rewardsByType: RewardByType<T> = {
        cash: [],
        box: [],
        card: [],
        total: 0,
      };
      const unclaimedRewardsByType: RewardByType<T> = {
        cash: [],
        box: [],
        card: [],
        total: 0,
      };
      const claimableRewardsByType: RewardByType<T> = {
        cash: [],
        box: [],
        card: [],
        total: 0,
      };

      so5Rewards.forEach(reward => {
        if (reward.probabilisticBundle) {
          rewardsByType.box.push(reward);
          if (reward.aasmState !== 'claimed') {
            unclaimedRewardsByType.box.push(reward);
            if (phoneNumberVerified) {
              claimableRewardsByType.box.push(reward);
            }
          }
        }
        if (reward.amount) {
          const { referenceCurrency } = reward.amount;
          rewardsByType.cash.push(reward);
          if (reward.aasmState !== 'claimed') {
            unclaimedRewardsByType.cash.push(reward);
            if (kycCompleted || referenceCurrency === SupportedCurrency.WEI) {
              claimableRewardsByType.cash.push(reward);
            }
          }
        }
        if (reward.rewardCards.length > 0) {
          const { rewardCards } = reward;
          rewardsByType.card.push(reward);
          if (reward.aasmState !== 'claimed') {
            unclaimedRewardsByType.card.push(reward);
            if (phoneNumberVerified || rewardCards.every(cardsAreCommon)) {
              claimableRewardsByType.card.push(reward);
            }
          }
        }
      });

      rewardsByType.total =
        rewardsByType.box.length +
        rewardsByType.card.length +
        rewardsByType.cash.length;
      unclaimedRewardsByType.total =
        unclaimedRewardsByType.box.length +
        unclaimedRewardsByType.card.length +
        unclaimedRewardsByType.cash.length;
      claimableRewardsByType.total =
        claimableRewardsByType.box.length +
        claimableRewardsByType.card.length +
        claimableRewardsByType.cash.length;

      const needVerifiedPhoneNumber =
        rewardsByType.box.length > 0 ||
        !rewardsByType.card.every(rewardsAreCommon);
      const needVerifiedAccount = rewardsByType.cash.some(
        ({ amount }) => amount?.referenceCurrency !== SupportedCurrency.WEI
      );

      return {
        rewardsByType,
        unclaimedRewardsByType,
        claimableRewardsByType,
        verificationNeeded: {
          kyc: needVerifiedAccount && !kycCompleted,
          phoneNumber: needVerifiedPhoneNumber && !phoneNumberVerified,
        },
      };
    }, [kycCompleted, phoneNumberVerified, so5Rewards]);
  },
  {
    so5Reward: gql`
      fragment useRewardsVerification_so5Reward on So5Reward {
        slug
        id
        aasmState
        amount {
          referenceCurrency
        }
        rewardCards {
          id
          anyCard {
            slug
            rarityTyped
          }
        }
        probabilisticBundle {
          id
        }
      }
    ` as TypedDocumentNode<useRewardsVerification_so5Reward>,
  }
);
